import React, { useEffect, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';

import { ReactComponent as UploadFileIcon } from '@/assets/img/upload-pet-image.svg';
import { DayValue, Day } from 'react-modern-calendar-datepicker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';

import moment from 'moment';
import './InsuranceFieldComponent.scss';
import { showNotification } from '@/utils/notificationUtils';
import { ReactComponent as PdfIcon } from '@/assets/img/pdf-icon.svg';
import { ReactComponent as DeleteIcon } from '@/assets/img/delete-icon-v2.svg';

import { Loader } from '@/components/Loader/Loader';
import { UploadFileResponse, useUploadFile } from '../api/getFields';
import DatePickerWithPortal from '@/components/DatePicker/DatepickerWithPortal';
import { t } from 'i18next';
import WebcamModal from '@/components/WebCam/WebCam';
import HybridFileUploader from '@/components/FileUploader/FileUploader';

type Props = {
  data?: Insurance;
  onChange?: (data: Insurance) => void;
  disabled?: boolean;
};

export type Insurance = {
  insuranceName?: string;
  insuranceExpiry?: string;
  insuranceInformation?: string;
  insuranceCopy?: string;
  fileName?: string;
  fileSize?: string;
};

export const InsuranceFieldComponent = ({ data, disabled, onChange }: Props) => {
  const [insuranceData, setInsuranceData] = useState<Insurance>();
  const uploadPictureMutation = useUploadFile();
  const [fileResponse, setFileResponse] = useState<UploadFileResponse>();
  useEffect(() => {
    if (data) {
      setInsuranceData(data);
    } else {
      setInsuranceData({
        insuranceName: '',
      });
    }
  }, [data]);
  const [day, setDay] = React.useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);

  const validateFile = (file: File) => {
    if (file.size > 10000000) {
      showNotification({
        type: 'info',
        title: 'Uploaded file should be less than 10 mb .',
      });
      return false;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

    if (!validTypes.includes(file.type)) {
      showNotification({
        type: 'info',
        title: 'Only png, jpg images or pdf file is allowed',
      });
      return false;
    }

    return true;
  };

  const onFileSelect = (file: File) => {
    uploadPetDocuments(file);
  };
  const uploadPetDocuments = async (selectedFiles: any) => {
    if (selectedFiles) {
      const formData = new FormData();
      formData.append('file', selectedFiles);
      const response = await uploadPictureMutation.mutateAsync({
        file: formData,
      });
      setFileResponse(response);
      const data = { ...insuranceData };
      data.insuranceCopy = response.fileUrl;
      data.fileName = response.fileName;
      data.fileSize = response.fileSize;
      updateInsuranceData(data);
    }
  };

  const updateInsuranceData = (vaccineData: Insurance | any) => {
    onChange && onChange({ ...vaccineData });
  };

  const onFormValueChange = (type: string, value: string) => {
    if (insuranceData) {
      if (type === 'insuranceName') {
        insuranceData.insuranceName = value;
      }
      if (type === 'insuranceInformation') {
        insuranceData.insuranceInformation = value;
      }
      if (type === 'insuranceExpiry') {
        insuranceData.insuranceExpiry = value;
      }

      updateInsuranceData({ ...insuranceData });
    }
  };
  const onDeleteDoc = (e: any) => {
    e?.stopPropagation();
    e?.preventDefault();
    setFileResponse(undefined);
    const data = { ...insuranceData };
    data.insuranceCopy = '';
    updateInsuranceData(data);
  };
  const handleCapture = (file: File) => {
    uploadPetDocuments(file);
  };

  const isMobile = () => {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  return (
    <div className="pet-insurance">
      {!insuranceData?.insuranceCopy && (
        <div className={`file-upload-container ${insuranceData?.insuranceCopy ? '' : 'empty'}`}>
          <>
            <HybridFileUploader
              isLoading={uploadPictureMutation.isLoading}
              onFileSelect={onFileSelect}
              fuLabel="file-uploader"
              onlyFile={true}
              showLoader={true}
              validateFile={validateFile}
            />
            {isMobile() ? (
              <></>
            ) : (
              !uploadPictureMutation.isLoading && (
                <WebcamModal disabled={disabled} onCapture={handleCapture} />
              )
            )}
          </>
        </div>
      )}

      {insuranceData?.insuranceCopy && (
        <>
          <div className="cards">
            <a href={insuranceData.insuranceCopy} target="_blank" rel="noreferrer">
              <div className="insurance-card">
                <div className="left-details">
                  <PdfIcon />
                  <div className="file-details">
                    <p>{'Insurance Document'}</p>
                  </div>
                </div>
                <div className="right-action">
                  <DeleteIcon onClick={onDeleteDoc} />
                </div>
              </div>
            </a>
          </div>
          <div className="form-field">
            <label className="form-label">Enter insurance details below</label>
            <div className="pet-row">
              <div className="pet-col-6">
                <input
                  type="text"
                  placeholder="Details"
                  value={insuranceData.insuranceName}
                  disabled={disabled}
                  onChange={(e) => {
                    onFormValueChange('insuranceName', e.target.value);
                  }}
                  className={insuranceData?.insuranceName ? '' : 'empty'}
                />
              </div>
              <div className="pet-col-6">
                <DatePickerWithPortal
                  value={insuranceData?.insuranceExpiry}
                  onChange={(val: DayValue) => {
                    onFormValueChange('insuranceExpiry', `${val?.month}/${val?.day}/${val?.year}`);
                  }}
                  disabled={disabled}
                  placeholder={t('Select Date')}
                  maximumDate={day}
                  validateData={true}
                />
              </div>
            </div>
            <label className="form-label">Add some extra details below</label>

            <textarea
              rows={4}
              placeholder="Add note"
              disabled={disabled}
              value={insuranceData.insuranceInformation}
              onChange={(e) => {
                onFormValueChange('insuranceInformation', e.target.value);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};
